/**
 * MBA Lab — the Leadership Scenario Simulator's branching trees. Pure data,
 * no functions: simulator-model.js reads this and the Razor view never
 * hardcodes tree copy, matching the planner-modes.js precedent.
 *
 * Six original, multi-step branching scenarios, deeper than the six flat
 * SCENARIOS in simulator-scenarios.js. A seventh candidate ("a strong
 * performer misses two commitments in a row") was deliberately left out -
 * it would have substantially duplicated the already-published
 * "difficult-feedback" flat scenario rather than covering new ground.
 *
 * Every setup, choice, and debrief below is an original scenario written
 * for this tool - never presented as a VCU case or as having one "correct"
 * path. Each tree has at least one ending where the user does everything
 * reasonably and it still goes imperfectly (real conversations do), and the
 * two coaching-style trees (solveit, stuck) each include at least one point
 * where staying silent is the strongest available move, not just "do
 * nothing."
 *
 * Node shape:
 *   choice node: { id, kind: 'choice', stem, state?, options: [{ id, text, result?, next }] }
 *   write node:  { id, kind: 'write', stem, checks: [checkId, ...], next }
 *   end node:    { id, kind: 'end', outcome: 'resolved'|'partial'|'stalled'|'damaged', summary }
 * `checks` values are check ids from lib/message-checks.js.
 */

export const TREES = Object.freeze([
  {
    id: 'peer-undermines-publicly',
    title: 'A peer agrees in private, undermines in public',
    setup: 'A fellow manager agreed with your proposal when you ran it by them privately. In yesterday\'s cross-team meeting, they visibly contradicted it in front of senior leadership - without warning you first.',
    role: 'You are the manager whose proposal was undermined.',
    other: 'A peer manager who agrees privately and contradicts publicly.',
    tracks: ['reachingOut', 'diagnosis', 'outcome'],
    start: 'reactMoment',
    nodes: {
      reactMoment: {
        kind: 'choice',
        phase: 'Right after the meeting',
        stem: 'What\'s your first move?',
        options: [
          { id: 'a', text: 'Say something in the room, right then', result: 'You raise it immediately, in front of the same group.', next: 'callOutNow' },
          { id: 'b', text: 'Let it go and see if it happens again', result: 'You decide not to raise it this time.', next: 'letItGo' },
          { id: 'c', text: 'Ask for a private conversation afterward', result: 'You decide to address it, but not in the room.', next: 'askPrivate' },
        ],
      },
      callOutNow: {
        kind: 'end',
        outcome: 'damaged',
        summary: 'Calling it out live, in front of the same group, without warning, reads as a public ambush regardless of how reasonable the underlying point is. Your peer gets defensive in front of an audience, and the disagreement becomes the story instead of the proposal.',
      },
      letItGo: {
        kind: 'choice',
        phase: 'A week later',
        stem: 'It happens again, in a different meeting. Now what?',
        options: [
          { id: 'a', text: 'Ask for a private conversation now', next: 'askPrivate' },
          { id: 'b', text: 'Let it go again - it\'s not worth the conflict', next: 'avoidEnd' },
        ],
      },
      avoidEnd: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'The pattern continues unaddressed. Other people in the room have started noticing you get contradicted in public and are watching to see whether you\'ll do anything about it.',
      },
      askPrivate: {
        kind: 'write',
        phase: 'Asking for the conversation',
        stem: 'Write the message asking for a private conversation.',
        checks: ['avoidsYouStem', 'hasObservable', 'endsWithQuestion'],
        model: 'In yesterday\'s meeting, my proposal got a different response from you than in our conversation the day before - could we find twenty minutes this week to talk it through?',
        next: 'openConvo',
      },
      openConvo: {
        kind: 'choice',
        phase: 'Opening the conversation',
        stem: 'How do you open it?',
        options: [
          { id: 'a', text: 'Name the specific pattern - private agreement, public contradiction', next: 'askWhy' },
          { id: 'b', text: 'Ask generally how they feel the proposal is going', next: 'askGeneral' },
        ],
      },
      askGeneral: {
        kind: 'end',
        outcome: 'partial',
        summary: 'The conversation is pleasant, but the actual pattern - agreeing privately and contradicting publicly - never gets named directly, so there\'s no real confirmation anything will change next time.',
      },
      askWhy: {
        kind: 'write',
        phase: 'Asking what happened',
        stem: 'Ask a genuinely open question about the gap between the two conversations.',
        checks: ['isOpenQuestion', 'underSevenWords', 'noAdvice'],
        model: 'What changed since our conversation?',
        next: 'theirAnswer',
      },
      theirAnswer: {
        kind: 'choice',
        phase: 'Their response',
        stem: 'What do they say?',
        state: 'A little caught off guard, but listening.',
        options: [
          { id: 'a', text: '(they admit someone pushed back hard in the room and they didn\'t want to be seen defending it)', next: 'realReason' },
          { id: 'b', text: '(they deny it happened at all)', next: 'denial' },
        ],
      },
      denial: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'A flat denial closes the conversation without resolution. The pattern likely continues, and there\'s now some added tension about having raised it at all.',
      },
      realReason: {
        kind: 'choice',
        phase: 'After the real reason surfaces',
        stem: 'Now what?',
        options: [
          { id: 'a', text: 'Agree on what happens next time this comes up', next: 'agreePlan' },
          { id: 'b', text: 'Accept the explanation and move on without a plan', next: 'noPlan' },
        ],
      },
      noPlan: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Understanding what happened is real progress, but without a concrete agreement for next time, there\'s a genuine chance the same thing happens again under the same pressure.',
      },
      agreePlan: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'A specific plan for next time - "if you disagree in the room, say so, rather than nodding along and raising it later" - gives both of you something concrete to hold each other to, not just a good feeling from one conversation.',
      },
    },
    debrief: {
      resolved: 'Naming the specific pattern, asking why rather than assuming, and agreeing on something concrete for next time is what actually prevents a repeat - understanding alone rarely does.',
      partial: 'Something real happened in this conversation, but without either a named pattern or a concrete plan, the same gap has room to open again.',
      stalled: 'Avoidance or denial both leave the actual pattern exactly where it started - the cost just moves later and gets paid by whoever notices next.',
      damaged: 'A public confrontation, even about something real, tends to cost more trust than it recovers of the original point.',
    },
  },

  {
    id: 'direct-report-cries',
    title: 'Three minutes in, they start crying',
    setup: 'You\'re giving a direct report developmental feedback about a specific, real miss. About three minutes in, mid-sentence, they start crying.',
    role: 'You are their manager, delivering the feedback.',
    other: 'A direct report who becomes visibly emotional partway through.',
    tracks: ['presence', 'continuation', 'outcome'],
    start: 'cryMoment',
    nodes: {
      cryMoment: {
        kind: 'choice',
        phase: 'The moment it happens',
        stem: 'What\'s your first move?',
        options: [
          { id: 'a', text: 'Keep going with the feedback as planned', next: 'pushThrough' },
          { id: 'b', text: 'Immediately apologize and drop the feedback entirely', next: 'retreat' },
          { id: 'c', text: 'Pause and ask directly what they need right now', next: 'pauseCheck' },
        ],
      },
      pushThrough: {
        kind: 'end',
        outcome: 'damaged',
        summary: 'Continuing past visible distress reads as not noticing, or not caring, even if every word of the feedback was fair. Whatever you say after this point mostly doesn\'t land.',
      },
      retreat: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'Withdrawing the feedback protects the moment but leaves the original, real issue completely unaddressed - it\'s very likely to resurface, in exactly the same shape, before long.',
      },
      pauseCheck: {
        kind: 'write',
        phase: 'Checking in',
        stem: 'Ask directly whether they want to pause or keep going.',
        checks: ['isOpenQuestion', 'underSevenWords', 'noAdvice'],
        model: 'What would help you right now?',
        next: 'theirChoice',
      },
      theirChoice: {
        kind: 'choice',
        phase: 'Their answer',
        stem: 'What do they say?',
        options: [
          { id: 'a', text: '"I want to keep going"', next: 'keepGoing' },
          { id: 'b', text: '"I need a few minutes"', next: 'givenSpace' },
        ],
      },
      givenSpace: {
        kind: 'choice',
        phase: 'After a short pause',
        stem: 'What next?',
        options: [
          { id: 'a', text: 'Ask whether they want to continue today or pick this up another time', next: 'resumeAsk' },
          { id: 'b', text: 'Assume it\'s kinder to just reschedule without asking', next: 'assumeReschedule' },
        ],
      },
      assumeReschedule: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Rescheduling might genuinely be the right call, or it might read as you deciding on their behalf that they couldn\'t handle it - a reasonable instinct that skipped an actual check.',
      },
      resumeAsk: {
        kind: 'write',
        phase: 'Offering the choice',
        stem: 'Ask a specific, low-pressure question offering the real choice.',
        checks: ['isOpenQuestion', 'underSevenWords'],
        model: 'What would you like to do?',
        next: 'theirResumeAnswer',
      },
      theirResumeAnswer: {
        kind: 'choice',
        phase: 'Their answer',
        stem: 'What do they choose?',
        options: [
          { id: 'a', text: 'They choose to continue now', next: 'keepGoing' },
          { id: 'b', text: 'They ask to reschedule', next: 'rescheduleAgreed' },
        ],
      },
      rescheduleAgreed: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'Rescheduling by their own choice, with a real date set before you end the conversation, respects both the content and the moment - the feedback still happens, just not while they\'re overwhelmed.',
      },
      keepGoing: {
        kind: 'choice',
        phase: 'Continuing',
        stem: 'How do you continue?',
        options: [
          { id: 'a', text: 'Acknowledge what just happened before you continue', next: 'acknowledgeFirst' },
          { id: 'b', text: 'Continue as if nothing happened', next: 'ignoreIt' },
        ],
      },
      ignoreIt: {
        kind: 'end',
        outcome: 'partial',
        summary: 'The feedback gets delivered, but skipping past the moment entirely can feel dismissive even when they were the one who chose to continue.',
      },
      acknowledgeFirst: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'A brief, genuine acknowledgment - "that clearly landed hard, thank you for staying with this" - before continuing respects what just happened without making it the whole conversation.',
      },
    },
    debrief: {
      resolved: 'Checking in directly, then actually following their answer - not your assumption about what\'s kindest - is what let the real feedback and the real moment both get their due.',
      partial: 'A reasonable instinct, acted on without checking it against what they actually wanted, is still a guess - sometimes a good one, sometimes not.',
      stalled: 'Protecting the moment at the cost of the content just delays the same conversation, usually without changing how hard it is next time.',
      damaged: 'Visible distress is information about timing, not a reason to abandon a fair point - but it does mean the delivery has to adjust to be heard at all.',
    },
  },

  {
    id: 'feedback-upward-to-your-boss',
    title: 'Telling your boss critical feedback',
    setup: 'Your manager has a habit that\'s genuinely affecting the team - most recently, presenting your team\'s work to senior leadership without naming who actually did it. You\'ve decided to raise it.',
    role: 'You are giving feedback to your own manager.',
    other: 'Your manager, who has the habit in question.',
    tracks: ['preparation', 'delivery', 'outcome'],
    start: 'prepChoice',
    nodes: {
      prepChoice: {
        kind: 'choice',
        phase: 'Before the conversation',
        stem: 'How do you prepare?',
        options: [
          { id: 'a', text: 'Write out the specific instance before the conversation', next: 'prepSpecific' },
          { id: 'b', text: 'Just raise it and see how the conversation goes', next: 'prepNone' },
        ],
      },
      prepNone: {
        kind: 'choice',
        phase: 'Mid-conversation, unprepared',
        stem: 'Without specifics ready, what do you do?',
        options: [
          { id: 'a', text: 'Fall back on a general impression instead', next: 'generalComplaint' },
          { id: 'b', text: 'Ask to pick this up once you\'re actually prepared', next: 'rescheduleUp' },
        ],
      },
      generalComplaint: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'A vague concern - "I feel like the team doesn\'t always get credit" - is easy to wave off or read as a mood rather than a pattern. Nothing about the actual habit changes.',
      },
      rescheduleUp: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Recognizing you\'re not ready and asking for more time is honest and defensible, but it costs a little momentum and delays the actual issue.',
      },
      prepSpecific: {
        kind: 'write',
        phase: 'Preparing the specific instance',
        stem: 'Write the specific instance you\'ll open with.',
        checks: ['hasObservable', 'avoidsYouStem'],
        model: 'In Tuesday\'s leadership review, the migration project was presented without naming which of us built it.',
        next: 'openUp',
      },
      openUp: {
        kind: 'choice',
        phase: 'Opening the conversation',
        stem: 'How do you open it?',
        options: [
          { id: 'a', text: 'Ask if now\'s a reasonable time for a slightly harder conversation', next: 'askTiming' },
          { id: 'b', text: 'Start with the issue directly, no framing', next: 'noFraming' },
        ],
      },
      askTiming: {
        kind: 'choice',
        phase: 'Checking timing first',
        stem: 'What do they say?',
        options: [
          { id: 'a', text: 'They agree to talk now', next: 'askForChange' },
          { id: 'b', text: 'They ask to reschedule', next: 'rescheduleAgreedUp' },
        ],
      },
      rescheduleAgreedUp: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Respecting their timing is reasonable, but the conversation is now deferred, and a deferred hard conversation can quietly slip further than either of you intends.',
      },
      noFraming: {
        kind: 'choice',
        phase: 'Their immediate reaction',
        stem: 'How do they react?',
        options: [
          { id: 'a', text: 'Defensive right away', next: 'defensiveUp' },
          { id: 'b', text: 'Surprised, but willing to hear it', next: 'askForChange' },
        ],
      },
      defensiveUp: {
        kind: 'write',
        phase: 'Meeting defensiveness',
        stem: 'Ask a genuinely open question about their side rather than pushing further.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What was going through your mind in that meeting?',
        next: 'afterDefense',
      },
      afterDefense: {
        kind: 'choice',
        phase: 'After the open question',
        stem: 'How do they respond?',
        options: [
          { id: 'a', text: 'They soften once actually asked', next: 'askForChange' },
          { id: 'b', text: 'They stay defensive and end the conversation', next: 'shutDown' },
        ],
      },
      shutDown: {
        kind: 'end',
        outcome: 'damaged',
        summary: 'Raising real, fair feedback upward and having it shut down costs you something even when you were right to raise it. The relationship absorbs that cost, not just the issue.',
      },
      askForChange: {
        kind: 'write',
        phase: 'Making the ask',
        stem: 'Make a specific, forward-looking request.',
        checks: ['hasRequest', 'endsWithQuestion'],
        model: 'Going forward, could you name who worked on something when you present it - would that work?',
        next: 'outcomeUp',
      },
      outcomeUp: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'A specific request, actually heard without defensiveness, is the version most likely to change the pattern - though a habit this established may take more than one reminder to fully stick.',
      },
    },
    debrief: {
      resolved: 'A prepared, specific instance and a concrete forward-looking request - not a vague impression - is what gave this the best chance of actually changing a manager\'s habit, not just venting about it.',
      partial: 'Deferring or under-preparing a hard upward conversation is often the safer short-term move, at the cost of the issue staying open longer.',
      stalled: 'Feedback without a specific instance behind it is easy to hear as mood rather than pattern, upward most of all.',
      damaged: 'Even fair, well-prepared feedback can be shut down by someone with more positional power - that risk is real, and it doesn\'t mean the feedback was wrong to raise.',
    },
  },

  {
    id: 'someone-wants-you-to-solve-it',
    title: 'Someone brings you a problem they want you to solve',
    setup: 'A direct report lays out a real, moderately complex problem, then looks at you expectantly - clearly wanting you to just tell them the answer.',
    role: 'You are their manager, in a coaching moment.',
    other: 'A direct report who wants a direct answer.',
    tracks: ['advice', 'questionQuality', 'commitment'],
    start: 'firstMove',
    nodes: {
      firstMove: {
        kind: 'choice',
        phase: 'The expectant pause',
        stem: 'What\'s your first move?',
        options: [
          { id: 'a', text: 'Tell them what you\'d do', next: 'giveAdvice' },
          { id: 'b', text: 'Ask what they\'ve already considered', next: 'askConsidered' },
          { id: 'c', text: 'Say nothing for a moment and let the silence sit', next: 'silenceFirst' },
        ],
      },
      giveAdvice: {
        kind: 'end',
        outcome: 'partial',
        summary: 'The advice might even be right, but they leave with your answer rather than their own judgment - and they\'ll likely be back with the next version of this exact problem soon.',
      },
      silenceFirst: {
        kind: 'choice',
        phase: 'After the pause',
        stem: 'What happens?',
        state: 'They look uncertain, then start talking again on their own.',
        options: [
          { id: 'a', text: 'They start actually thinking out loud', next: 'askConsidered' },
          { id: 'b', text: 'They wait you out and ask again what you think', next: 'waitedOut' },
        ],
      },
      waitedOut: {
        kind: 'choice',
        phase: 'They ask again',
        stem: 'Now what?',
        options: [
          { id: 'a', text: 'Ask what they\'ve already considered', next: 'askConsidered' },
          { id: 'b', text: 'Give in and just tell them', next: 'giveAdvice' },
        ],
      },
      askConsidered: {
        kind: 'write',
        phase: 'Asking what they\'ve tried',
        stem: 'Ask what they\'ve already considered or tried.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What have you already thought about here?',
        next: 'askGoal',
      },
      askGoal: {
        kind: 'choice',
        phase: 'After hearing what they\'ve considered',
        stem: 'What do you ask next?',
        options: [
          { id: 'a', text: 'What would a genuinely good outcome look like here?', next: 'askOptions' },
          { id: 'b', text: 'Just ask if they\'ve decided yet', next: 'rushToDecide' },
        ],
      },
      rushToDecide: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'Moving straight to a decision without exploring the goal or the real options tends to produce a thin, low-conviction choice - they picked something, not necessarily the strongest available option.',
      },
      askOptions: {
        kind: 'write',
        phase: 'Generating options',
        stem: 'Ask what other options exist, genuinely open to whatever they name.',
        checks: ['isOpenQuestion', 'underSevenWords'],
        model: 'What else could you try?',
        next: 'theyGenerateOptions',
      },
      theyGenerateOptions: {
        kind: 'choice',
        phase: 'They name two or three real options',
        stem: 'What next?',
        options: [
          { id: 'a', text: 'Ask which one they\'re leaning toward, and why', next: 'askCommit' },
          { id: 'b', text: 'Tell them which one you\'d pick', next: 'pickForThem' },
        ],
      },
      pickForThem: {
        kind: 'end',
        outcome: 'damaged',
        summary: 'Once you name a favorite, it stops being their decision, even phrased gently - and after real exploration was already happening, it can land as a bait-and-switch: "you asked me to think it through, then told me anyway."',
      },
      askCommit: {
        kind: 'write',
        phase: 'Getting a commitment',
        stem: 'Ask for their choice and a date to check back.',
        checks: ['hasRequest', 'endsWithQuestion'],
        model: 'Which one are you going with, and would you set a date for us to check back?',
        next: 'outcomeSolve',
      },
      outcomeSolve: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'They leave with their own decision and a real follow-up date - even if it\'s not the exact plan you\'d have picked, it\'s one they\'ll actually follow through on, because it\'s theirs.',
      },
    },
    debrief: {
      resolved: 'Asking what they\'d already considered, then their goal, then their options, then their actual commitment - in that order - is what turned "tell me the answer" into their own decision.',
      partial: 'A direct answer solves today\'s problem and skips the chance to build the judgment that would solve the next one without you.',
      stalled: 'Skipping straight to "have you decided" without exploring the goal or real options tends to produce a decision that doesn\'t hold under pressure.',
      damaged: 'Coaching someone through their own options and then overriding them at the end can cost more trust than just giving the advice up front would have.',
    },
  },

  {
    id: 'high-performer-might-leave',
    title: 'A high performer, considering leaving',
    setup: 'You\'ve heard, secondhand, that one of your strongest performers had coffee with a former colleague who now works at a competitor. They haven\'t brought it up with you.',
    role: 'You are their manager.',
    other: 'A strong performer who may be weighing other options.',
    tracks: ['directness', 'listening', 'outcome'],
    start: 'hearAbout',
    nodes: {
      hearAbout: {
        kind: 'choice',
        phase: 'After hearing secondhand',
        stem: 'How do you first raise it?',
        options: [
          { id: 'a', text: 'Ask them directly if they\'re considering leaving', next: 'askDirect' },
          { id: 'b', text: 'Say nothing and hope it passes', next: 'sayNothing' },
          { id: 'c', text: 'Preemptively offer a raise or promotion', next: 'preemptOffer' },
        ],
      },
      sayNothing: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'Avoiding the conversation doesn\'t make the decision go away - it just means you find out once it\'s already final, with no chance to respond to whatever\'s actually driving it.',
      },
      preemptOffer: {
        kind: 'end',
        outcome: 'partial',
        summary: 'A preemptive offer might work in the short term, but if the real driver isn\'t money, it buys a few months at most before the same conversation resurfaces.',
      },
      askDirect: {
        kind: 'choice',
        phase: 'They confirm it',
        stem: 'They say yes, they\'ve been thinking about it. What next?',
        options: [
          { id: 'a', text: 'Ask what\'s actually driving it', next: 'askWhy' },
          { id: 'b', text: 'Start countering immediately with reasons to stay', next: 'counterImmediately' },
        ],
      },
      counterImmediately: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Arguing your case before understanding theirs risks solving the wrong problem entirely - the reasons you\'d offer may not touch whatever\'s actually driving this.',
      },
      askWhy: {
        kind: 'write',
        phase: 'Asking what\'s driving it',
        stem: 'Ask a genuinely open question about what\'s behind it.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What\'s making you think about it?',
        next: 'theirReason',
      },
      theirReason: {
        kind: 'choice',
        phase: 'Their answer',
        stem: 'What do they say?',
        options: [
          { id: 'a', text: 'It\'s mostly about feeling stuck, wanting real growth', next: 'growthReason' },
          { id: 'b', text: 'It\'s mostly about money', next: 'moneyReason' },
          { id: 'c', text: 'It\'s not really fixable - a life change, a move', next: 'notFixable' },
        ],
      },
      moneyReason: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Money is real and worth addressing directly - but treating it as the whole story without asking further tends to under-diagnose what else might be underneath it.',
      },
      notFixable: {
        kind: 'choice',
        phase: 'Hearing something you can\'t change',
        stem: 'Now what?',
        options: [
          { id: 'a', text: 'Let the silence hold the moment rather than filling it', next: 'silenceHold' },
          { id: 'b', text: 'Immediately pitch reasons to stay anyway', next: 'pitchAnyway' },
        ],
      },
      pitchAnyway: {
        kind: 'end',
        outcome: 'damaged',
        summary: 'Pushing to stay against a reason that was never about the job reads as not actually having listened, and can sour what would otherwise have been a good departure.',
      },
      silenceHold: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'Sometimes the right response to an unfixable reason is simply to hear it, without trying to fix what was never yours to fix - the relationship stays intact even though the person leaves.',
      },
      growthReason: {
        kind: 'write',
        phase: 'Following the real thread',
        stem: 'Ask what growth would actually look like for them, specifically.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What would real growth actually look like for you here?',
        next: 'growthPlan',
      },
      growthPlan: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'A specific, real conversation about what growth means to them - even without an immediate title change - often matters more than expected, as long as it\'s followed by real action, not just the conversation itself.',
      },
    },
    debrief: {
      resolved: 'Asking directly, then genuinely open to whatever answer comes back - including one you can\'t fix - is what let this go as well as it could, whether they stayed or left.',
      partial: 'Countering before understanding, or treating one stated reason as the whole story, both risk solving a problem that isn\'t the real one.',
      stalled: 'Silence from you doesn\'t produce silence from the situation - it just moves the moment you find out to the least useful possible time.',
      damaged: 'Arguing against a reason that was never within your control to change tends to cost more than it saves.',
    },
  },

  {
    id: 'peer-stuck-on-a-decision',
    title: 'A peer, stuck on the same decision, again',
    setup: 'A peer has brought you the exact same undecided question for the fourth time, clearly hoping you\'ll just tell them what to do.',
    role: 'You are their peer, in an informal coaching moment.',
    other: 'A peer who has been stuck on one decision across several conversations.',
    tracks: ['advice', 'questionQuality', 'commitment'],
    start: 'fourthTime',
    nodes: {
      fourthTime: {
        kind: 'choice',
        phase: 'The fourth time it comes up',
        stem: 'What\'s your first move?',
        options: [
          { id: 'a', text: 'Just tell them what you\'d do - it\'s the fourth time', next: 'giveAdviceStuck' },
          { id: 'b', text: 'Name, without judgment, that this is the fourth time', next: 'nameThePattern' },
          { id: 'c', text: 'Ask them to just decide right now, out loud', next: 'forceDecision' },
        ],
      },
      giveAdviceStuck: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Telling them your answer on round four might finally end the loop, but it\'s now your decision, not theirs - and if it goes wrong, that\'s what they\'ll remember.',
      },
      forceDecision: {
        kind: 'end',
        outcome: 'stalled',
        summary: 'Pressure to decide on the spot, after three rounds of genuine stuckness, tends to produce a forced, low-conviction answer that doesn\'t actually hold once the pressure is off.',
      },
      nameThePattern: {
        kind: 'write',
        phase: 'Naming it',
        stem: 'Name, without judgment, that this is the fourth conversation about the same thing.',
        checks: ['avoidsYouStem', 'hasObservable'],
        model: 'This is the fourth time this has come up between us.',
        next: 'theirResponse',
      },
      theirResponse: {
        kind: 'choice',
        phase: 'Their reaction',
        stem: 'How do they respond to being named?',
        options: [
          { id: 'a', text: 'They get defensive about being called out', next: 'defensiveStuck' },
          { id: 'b', text: 'They acknowledge it, almost relieved someone said it', next: 'realBlocker' },
        ],
      },
      defensiveStuck: {
        kind: 'write',
        phase: 'Meeting the defensiveness',
        stem: 'Ask a genuinely open question about what\'s actually making this hard to decide.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What\'s actually making this one hard?',
        next: 'realBlocker',
      },
      realBlocker: {
        kind: 'choice',
        phase: 'What turns out to be the real blocker',
        stem: 'What do they say?',
        options: [
          { id: 'a', text: 'A specific fear of a bad outcome if they choose wrong', next: 'fearReason' },
          { id: 'b', text: 'A genuine, specific gap in information', next: 'infoGap' },
        ],
      },
      infoGap: {
        kind: 'write',
        phase: 'Naming the missing piece',
        stem: 'Ask exactly what information is still missing.',
        checks: ['isOpenQuestion', 'noAdvice'],
        model: 'What specifically do you still need to know, and by when?',
        next: 'infoPlan',
      },
      infoPlan: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'Naming the specific missing piece turns an abstract "I\'m stuck" into a concrete next step with a real deadline - the loop breaks because there\'s finally something to go do.',
      },
      fearReason: {
        kind: 'choice',
        phase: 'Naming the fear',
        stem: 'Now what?',
        options: [
          { id: 'a', text: 'Sit with it in silence for a moment instead of jumping to reassure', next: 'silenceStuck' },
          { id: 'b', text: 'Immediately reassure them it\'ll be fine', next: 'reassureStuck' },
        ],
      },
      reassureStuck: {
        kind: 'end',
        outcome: 'partial',
        summary: 'Reassurance feels supportive in the moment but doesn\'t actually address the specific fear - the same hesitation is likely to show up again next time something similar comes up.',
      },
      silenceStuck: {
        kind: 'end',
        outcome: 'resolved',
        summary: 'Giving the fear room to be said out loud, without rushing to fix it, is often what actually lets someone move past it - not the reassurance itself.',
      },
    },
    debrief: {
      resolved: 'Naming the pattern, then following whatever real blocker surfaced - a fear or a genuine information gap - with a question suited to that specific blocker, is what finally moved this past round four.',
      partial: 'Advice or reassurance both feel like help in the moment, and both tend to leave the actual blocker exactly where it was.',
      stalled: 'Forcing a decision before the real blocker is named usually produces a decision that doesn\'t survive contact with the same doubt that\'s been circling for three rounds already.',
    },
  },
]);
